/**
 * 暗色基调预设。
 * 仅供 ColorLabTuner 使用，随工具一起移除。
 *
 * 「当前默认」中 registry 内的 token 直接取自 colorTokenRegistry 的 dark 值；
 * registry 之外的 token（导航栏 --fett-app-sider-nav-primary-bg、聊天区
 * --fett-chat-dark-surface）取自 themeVariables 中 dark 模式的生成值。
 * 其余预设参考行业主流深色产品的背景层级。
 */
import { getColorToken } from 'themes/colorTokenRegistry';

export interface TunableToken {
  name: string;
  label: string;
}

/** registry 之外、需要在「当前默认」中手动登记默认值的 token */
const NON_REGISTRY_DEFAULTS: BgColors = {
  '--fett-app-sider-nav-primary-bg': '#131720',
  '--fett-chat-dark-surface': '#10141b',
};

/**
 * 参与基调调整的背景 token（不含文字/边框）。
 * 被 darkModeColorAliases 接线到其他 token 的（--fett-chat-*-bg、
 * --fett-conv-sider-head-bg）不在此列——调它们的源 token 即可。
 */
export const TUNABLE_TOKENS: TunableToken[] = [
  { name: '--fett-bg-app', label: '应用底色' },
  { name: '--fett-app-sider-nav-primary-bg', label: '导航栏' },
  { name: '--fett-bg-sider', label: '侧边栏' },
  { name: '--fett-bg-panel', label: '面板' },
  { name: '--fett-bg-subtle', label: '次层面板' },
  { name: '--fett-bg-elevated', label: '浮起面板' },
  { name: '--fett-bg-hover', label: '悬停背景' },
  { name: '--fett-bg-input', label: '输入框' },
  { name: '--fett-chat-dark-surface', label: '聊天区背景' },
];

export type BgColors = Record<string, string>;

export const DEFAULT_DARK: BgColors = {
  ...NON_REGISTRY_DEFAULTS,
  ...Object.fromEntries(
    TUNABLE_TOKENS.filter(({ name }) => !(name in NON_REGISTRY_DEFAULTS)).map(({ name }) => [
      name,
      getColorToken(name)?.dark ?? '#000000',
    ]),
  ),
};

export interface BgPreset {
  id: string;
  name: string;
  /** 参考来源，展示用 */
  source: string;
  colors: BgColors;
}

export const PRESETS: BgPreset[] = [
  {
    id: 'current',
    name: '当前默认',
    source: 'colorTokenRegistry',
    colors: DEFAULT_DARK,
  },
  {
    // 高对比荧光调试色：配合 scripts/color-lab-token-layout.cjs 出布局图，
    // 用于确认每个 token 实际渲染在哪些区块，非设计方案
    id: 'debug',
    name: '反差调试',
    source: 'Debug high-contrast',
    colors: {
      '--fett-bg-app': '#ff00ff',
      '--fett-app-sider-nav-primary-bg': '#00e5ff',
      '--fett-bg-sider': '#76ff03',
      '--fett-bg-panel': '#ff9100',
      '--fett-bg-subtle': '#7c4dff',
      '--fett-bg-elevated': '#ffea00',
      '--fett-bg-hover': '#ff1744',
      '--fett-bg-input': '#00e676',
      '--fett-chat-dark-surface': '#ff4081',
    },
  },
  {
    id: 'github',
    name: 'GitHub',
    source: 'GitHub Dark',
    colors: {
      '--fett-bg-app': '#010409',
      '--fett-app-sider-nav-primary-bg': '#0d1117',
      '--fett-bg-sider': '#0d1117',
      '--fett-bg-panel': '#161b22',
      '--fett-bg-subtle': '#0d1117',
      '--fett-bg-elevated': '#21262d',
      '--fett-bg-hover': '#21262d',
      '--fett-bg-input': '#0d1117',
      '--fett-chat-dark-surface': '#0c1016',
    },
  },
  {
    id: 'vscode',
    name: 'VS Code',
    source: 'VS Code Dark+',
    colors: {
      '--fett-bg-app': '#1e1e1e',
      '--fett-app-sider-nav-primary-bg': '#333333',
      '--fett-bg-sider': '#252526',
      '--fett-bg-panel': '#252526',
      '--fett-bg-subtle': '#1f1f1f',
      '--fett-bg-elevated': '#2d2d30',
      '--fett-bg-hover': '#2a2d2e',
      '--fett-bg-input': '#3c3c3c',
      '--fett-chat-dark-surface': '#222222',
    },
  },
  {
    id: 'discord',
    name: 'Discord',
    source: 'Discord Dark',
    colors: {
      '--fett-bg-app': '#313338',
      '--fett-app-sider-nav-primary-bg': '#1e1f22',
      '--fett-bg-sider': '#2b2d31',
      '--fett-bg-panel': '#2b2d31',
      '--fett-bg-subtle': '#2e3035',
      '--fett-bg-elevated': '#383a40',
      '--fett-bg-hover': '#35373c',
      '--fett-bg-input': '#383a40',
      '--fett-chat-dark-surface': '#2e3034',
    },
  },
  {
    id: 'linear',
    name: 'Linear',
    source: 'Linear Dark',
    colors: {
      '--fett-bg-app': '#08090a',
      '--fett-app-sider-nav-primary-bg': '#101112',
      '--fett-bg-sider': '#101112',
      '--fett-bg-panel': '#141517',
      '--fett-bg-subtle': '#0c0d0e',
      '--fett-bg-elevated': '#1c1d20',
      '--fett-bg-hover': '#232529',
      '--fett-bg-input': '#101112',
      '--fett-chat-dark-surface': '#0e0f10',
    },
  },
  // 以下为聊天/IM 类产品方案，层级映射：导航栏=顶栏/服务器栏，侧边栏=会话列表，
  // 面板=卡片/侧栏容器，聊天区背景取应用底色与面板的中间色
  {
    id: 'slack',
    name: 'Slack',
    source: 'Slack Dark（客服/协作聊天）',
    colors: {
      '--fett-bg-app': '#1a1d21',
      '--fett-app-sider-nav-primary-bg': '#121016',
      '--fett-bg-sider': '#19171d',
      '--fett-bg-panel': '#222529',
      '--fett-bg-subtle': '#1a1d21',
      '--fett-bg-elevated': '#383a3f',
      '--fett-bg-hover': '#27242c',
      '--fett-bg-input': '#22252a',
      '--fett-chat-dark-surface': '#1e2125',
    },
  },
  {
    id: 'whatsapp',
    name: 'WhatsApp',
    source: 'WhatsApp Web Dark（青灰调）',
    colors: {
      '--fett-bg-app': '#0b141a',
      '--fett-app-sider-nav-primary-bg': '#111b21',
      '--fett-bg-sider': '#111b21',
      '--fett-bg-panel': '#202c33',
      '--fett-bg-subtle': '#111b21',
      '--fett-bg-elevated': '#233138',
      '--fett-bg-hover': '#2a3942',
      '--fett-bg-input': '#2a3942',
      '--fett-chat-dark-surface': '#162026',
    },
  },
  {
    id: 'telegram',
    name: 'Telegram',
    source: 'Telegram Desktop Night（藏青调）',
    colors: {
      '--fett-bg-app': '#0e1621',
      '--fett-app-sider-nav-primary-bg': '#0e1621',
      '--fett-bg-sider': '#17212b',
      '--fett-bg-panel': '#17212b',
      '--fett-bg-subtle': '#0e1621',
      '--fett-bg-elevated': '#232e3c',
      '--fett-bg-hover': '#1e2c3a',
      '--fett-bg-input': '#17212b',
      '--fett-chat-dark-surface': '#121c26',
    },
  },
];
